import { useEffect } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useUnknownConfigProperty } from '@leagueplatform/core';
import { captureMessage } from '@leagueplatform/observability';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';
import { PERSONA_JUST_SELECTED_STORAGE_KEY } from 'common/persona-selector-signals';

const PERSONA_SELECTOR_FLAG_KEY = 'release_eos_persona_selector_enabled';

type GuardOutcome = 'passthrough' | 'redirect_setup';

const logGuardEvaluation = (params: {
  flagEnabled: boolean;
  justSelected: boolean;
  outcome: GuardOutcome;
}) => {
  captureMessage('eos_persona_selector_guard evaluation', {
    severityLevel: 'info',
    context: {
      guard: {
        component: 'eos_persona_selector_guard',
        flag_enabled: params.flagEnabled,
        just_selected: params.justSelected,
        outcome: params.outcome,
      },
    },
  });
};

const readJustSelectedFlag = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  return sessionStorage.getItem(PERSONA_JUST_SELECTED_STORAGE_KEY) === '1';
};

/**
 * Route guard that redirects to `/setup-market-experience` when the
 * `release_eos_persona_selector_enabled` flag is on and the SE has not yet
 * submitted the picker in this tab session. The just-selected signal is a
 * sessionStorage flag written by the `submitForm` action-handler override
 * (see `utils/init-masonry-action-handlers.ts`); it survives the on-success
 * deeplink to `/home` and everything that follows for the rest of the tab.
 *
 * - Flag OFF (default): passthrough. Direct URL navigation to
 *   `/setup-market-experience` still resolves — the URL-block for flag-off
 *   is deferred (MSP3-315).
 * - Flag ON, just-selected FALSE: redirect to the setup screen.
 * - Flag ON, just-selected TRUE: passthrough. The SE has already submitted
 *   this tab session; the demo continues on the guarded route (typically
 *   `/home`).
 */
export const RequireMarketAndPersona = () => {
  const flagEnabled = Boolean(
    useUnknownConfigProperty(PERSONA_SELECTOR_FLAG_KEY, false),
  );
  const justSelected = readJustSelectedFlag();
  const shouldRedirect = flagEnabled && !justSelected;
  const outcome: GuardOutcome = shouldRedirect
    ? 'redirect_setup'
    : 'passthrough';

  useEffect(() => {
    logGuardEvaluation({ flagEnabled, justSelected, outcome });
  }, [flagEnabled, justSelected, outcome]);

  if (shouldRedirect) {
    return <Navigate to={APP_MANAGED_ROUTES.SETUP_MARKET_EXPERIENCE} replace />;
  }
  return <Outlet />;
};
